import { Button, Interpolate } from '@wg/wows-react-uikit';
import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import {
    memo, useCallback, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Footer.scss';

import type { IReward } from '~/types';
import type { RootState } from '~/store';

import { useAppDispatch, useAppSelector } from '~/hooks';
import {
    makeCompensation,
    stopRepeatBuildingProcess as stopRepeatBuildingProcessAction,
} from '~/store/app';
import {
    openMultipleBuyDialog,
    openWelcomeDialog,
} from '~/store/dialogs';
import ProgressBar from '~/Components/ProgressBar/ProgressBar';
import { ProgressBarController } from '~/Components/ProgressBar/ProgressBarController';
import RefManager, { RefManagerKeys } from '~/refManager';
import { appIsReady, isCollectRewardButtonVisible, isStartBoosterVisible } from '~/selectors';
import { compensation } from '~/wowsEntities';

export interface FooterState {
    eventum: number,
    isCollectRewardButtonVisible: boolean,
    isBuyPremBoosterButtonVisible: boolean,
    isCompensationLabelVisible: boolean,
    hasAvailableRewards: boolean,
    hasPendingTransactions: boolean,
    appIsReady: boolean,
    isInGame: boolean,
    isRepeating: boolean,
    isBlueprintsViewerVisible: boolean,
    shipLevel: number,
    progress?: number | null,
    rewards: Array<IReward>,
    freeLevels: number,
    compensations: Array<CompensationItem>,
}

const stateSelector = (state: RootState): FooterState => {
    let allRewardsHasGameReward = true;
    state.app.rewards.forEach((reward) => {
        if (
            state.app.availableRewards.includes(reward.id)
            && allRewardsHasGameReward && reward.gameRewards.length === 0
        ) {
            allRewardsHasGameReward = false;
        }
    });
    const hasPendingTransactions: boolean = get(state.app, 'account.pendingTransactions', false);
    const eventum = get(state.app, 'account.wallet.eventum', 0) as number;
    const hasAvailableRewards = state.app.availableRewards.length > 0;
    const isCompensationLabelVisible = state.app.isEndReached && eventum > 0;
    const isBuyPremBoosterButtonVisible = !isStartBoosterVisible(state) && !state.app.isEndReached;

    return {
        eventum,
        isCollectRewardButtonVisible: isCollectRewardButtonVisible(state),
        isBuyPremBoosterButtonVisible,
        isCompensationLabelVisible,
        hasAvailableRewards,
        hasPendingTransactions,
        appIsReady: appIsReady(state),
        isInGame: state.app.isInGame,
        isRepeating: state.app.isRepeating,
        isBlueprintsViewerVisible: state.dialogs.isBlueprintsViewerVisible,
        shipLevel: state.app.shipLevel,
        progress: state.app.account.progress,
        rewards: state.app.rewards,
        freeLevels: state.app.freeLevels,
        compensations: state.app.compensations,
    };
};

const Footer = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isVisible, setIsVisible] = useState(false);
    const state = useAppSelector(stateSelector, equal);

    const {
        eventum,
        isCollectRewardButtonVisible,
        isBuyPremBoosterButtonVisible,
        isCompensationLabelVisible,
        hasAvailableRewards,
        hasPendingTransactions,
        appIsReady,
        isInGame,
        isRepeating,
        isBlueprintsViewerVisible,
        progress,
        freeLevels,
        compensations,
    } = state;

    const wrapperClasses = classnames(styles.wrapper, {
        [styles.wrapperVisible]: isVisible,
    });

    useEffect(() => {
        if (appIsReady && !isVisible) {
            setTimeout(
                () => {
                    setIsVisible(true);
                },
                isInGame ? 500 : 0,
            );
        }
    }, [appIsReady, isInGame, isVisible]);

    const stopRepeatBuildingProcess = useCallback(() => {
        dispatch(stopRepeatBuildingProcessAction(undefined, progress as number));
    }, [dispatch, progress]);

    const collectRewards = useCallback(() => {
        if (hasAvailableRewards) {
            dispatch(openWelcomeDialog(true));
        } else {
            dispatch(makeCompensation());
        }
    }, [dispatch, hasAvailableRewards]);

    const setProgressbarRef = useCallback((ref: ProgressBarController) => {
        RefManager.setRef(RefManagerKeys.ProgressBar, ref);
    }, []);

    const renderCompensationValue = () => {
        const compensationMultiplier = eventum;
        if (isCompensationLabelVisible) {
            return (
                <div className={styles.compensationValue}>
                    <Interpolate str={t('Доступная награда: %(compensation)s')} compensation={compensation(compensationMultiplier, compensations)} />
                </div>
            );
        }
        return null;
    };

    const handleOpenMultipleBuyDialog = useCallback(() => {
        dispatch(openMultipleBuyDialog());
    }, [dispatch]);

    return (
        <div className={wrapperClasses}>
            <div className={styles.content}>
                {!isBlueprintsViewerVisible && <div className={styles.rightMouseButton}></div>}
                {isRepeating ? (
                    <div className={styles.footerActions}>
                        <Button isFlat={true} onClick={stopRepeatBuildingProcess}>
                            {t('Остановить просмотр строительства')}
                        </Button>
                    </div>
                ) : (
                    <div className={styles.footerActions}>
                        {isBuyPremBoosterButtonVisible && (
                            <Button type={'orange'} isLarger={true} isFlat={true} onClick={handleOpenMultipleBuyDialog} isDisabled={hasPendingTransactions}>
                                {t('Завершить за дублоны')}
                            </Button>
                        )}

                        {isCollectRewardButtonVisible && !hasPendingTransactions && (
                            <div className={styles.compensationBlock}>
                                {renderCompensationValue()}
                                <Button isLarger={true} isFlat={true} onClick={collectRewards}>
                                    {t('Забрать награду')}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.progress} id="progressWrapper">
                    <ProgressBar ref={setProgressbarRef} />
                </div>
                <div className={styles.subtitleText}>
                    <Interpolate
                        str={t('Завершайте до %(count)s этапов строительства, выполняя Боевые задачи Верфи и получайте ценные награды!')}
                        count={freeLevels}
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(Footer);
